import { computed, reactive, ref } from "vue";
import { Loader } from "@googlemaps/js-api-loader";
import {
  COUNTRY_VALUE_ABREV_MAP,
  countryConfig,
} from "@/scripts/countries/countries.js";

const GOOGLE_MAPS = reactive({
  Place: null,
  AutocompleteSessionToken: null,
  AutocompleteSuggestion: null,
});

const loader = new Loader({
  apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  version: "weekly",
});

loader.load().then(async () => {
  const { Place, AutocompleteSessionToken, AutocompleteSuggestion } =
    await google.maps.importLibrary("places");
  GOOGLE_MAPS.Place = Place;
  GOOGLE_MAPS.AutocompleteSessionToken = AutocompleteSessionToken;
  GOOGLE_MAPS.AutocompleteSuggestion = AutocompleteSuggestion;
});
export const useGooglePlaces = () => {
  const results = ref([]);
  const isFetching = ref(false);

  async function fetchAutoCompleteAddresses(addressInput) {
    isFetching.value = true;

    const token = new GOOGLE_MAPS.AutocompleteSessionToken();
    const requestPayload = {
      input: addressInput,
      sessionToken: token,
      language: "en-US", // assuming english
      includedRegionCodes: ["us", "ca"],
    };

    const { suggestions } =
      await GOOGLE_MAPS.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        requestPayload
      );

    let addressResults = [];

    const places = suggestions.map((suggestion) =>
      suggestion.placePrediction.toPlace()
    );

    await Promise.allSettled(
      places.map((place) =>
        place.fetchFields({
          fields: ["formattedAddress", "addressComponents"],
        })
      )
    );

    places.forEach((place) => {
      addressResults.push({
        display: place.formattedAddress,
        address: place.addressComponents,
      });
    });

    isFetching.value = false;
    results.value = addressResults;

    return results.value;
  }

  function matchAddressDetail(address, detail) {
    return address.find((part) => part.Eg.includes(detail))?.longText;
  }

  const formattedResults = computed(() =>
    results.value.map((address) => {
      const enrollmentAddress = {
        address1: `${matchAddressDetail(address.address, "street_number")} ${matchAddressDetail(
          address.address,
          "route"
        )}`,
        address2: matchAddressDetail(address.address, "subpremise") || null,
        city:
          matchAddressDetail(address.address, "locality") ||
          matchAddressDetail(address.address, "postal_town") ||
          matchAddressDetail(address.address, "sublocality") ||
          matchAddressDetail(address.address, "colloquial_area") ||
          matchAddressDetail(address.address, "neighborhood") ||
          matchAddressDetail(address.address, "administrative_area_level_3") ||
          matchAddressDetail(address.address, "administrative_area_level_2") ||
          "",
        state:
          matchAddressDetail(address.address, "administrative_area_level_1") ||
          "",
        postal_code: matchAddressDetail(address.address, "postal_code") || null,
        country: matchAddressDetail(address.address, "country") || null,
      };

      let country = enrollmentAddress.country;
      if (country.length !== 2 && COUNTRY_VALUE_ABREV_MAP[country]) {
        country = COUNTRY_VALUE_ABREV_MAP[country];
      }
      let state = enrollmentAddress.state;
      if (state.length !== 2) {
        const stateList = countryConfig[country]?.addressLevelOneList;
        const stateAbbreviation = stateList?.find(
          (state) =>
            state.label === enrollmentAddress.state ||
            state.value === enrollmentAddress.state
        )?.value;
        if (stateAbbreviation) {
          state = stateAbbreviation;
        }
      }

      const enrollmentAddressShort = {
        ...enrollmentAddress,
        country: country,
        state: state,
      };

      return {
        ...address,
        enrollmentAddress,
        enrollmentAddressShort,
      };
    })
  );

  const clearAddressResults = () => {
    results.value = [];
  };

  return {
    isFetching,
    results,
    formattedResults,
    fetchAutoCompleteAddresses,
    clearAddressResults,
    matchAddressDetail,
    Place: GOOGLE_MAPS.Place,
    AutocompleteSessionToken: GOOGLE_MAPS.AutocompleteSessionToken,
    AutocompleteSuggestion: GOOGLE_MAPS.AutocompleteSuggestion,
  };
};
